// Libs
import classNames from 'classnames/bind';
import { Heading, Table, Text, View } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BasePagination, BaseSelect, Loading } from '~/components/common';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { DEFAULT_LIMIT, DEFAULT_LIMIT_PAGE, DEFAULT_PAGE } from '~/utils/constants/common';
import { IStudent } from '~/utils/interface/profile';
import { getStudents } from '~/thunks/user/userThunk';
import { IQueryGetStudent } from '~/utils/interface/account';
import { IBaseSelectOption } from '~/utils/interface/common';
import { getCourses } from '~/thunks/course/courseThunk';
// Styles, images, icons
import styles from './Student.module.scss';
import { icons } from '~/assets';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const Student = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'admin/student']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { students: studentsResponse } = useAppSelector((state) => state.userState);
  //#endregion Selector

  //#region Declare State
  const [students, setStudents] = useState<IStudent[]>([]);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [courseOptions, setCourseOptions] = useState<IBaseSelectOption[]>([]);
  const [courseSelected, setCourseSelected] = useState<string>();
  const [isLoadingOverlay, setIsLoadingOverlay] = useState<boolean>(true);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (courseSelected) {
      setPage(DEFAULT_PAGE);
      handleGetStudents({
        courseId: courseSelected,
        limit: DEFAULT_LIMIT_PAGE,
        page: DEFAULT_PAGE,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSelected]);

  useEffect(() => {
    setStudents(studentsResponse?.responses ?? []);
  }, [studentsResponse]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleBack = () => {
    navigate(-1);
  };

  const handleGetStudents = async (query?: IQueryGetStudent) => {
    setIsLoadingOverlay(true);
    await dispatch(
      getStudents(
        query ?? {
          limit: DEFAULT_LIMIT_PAGE,
          page: page,
        }
      )
    )
      .unwrap()
      .finally(() => {
        setIsLoadingOverlay(false);
      });
  };

  const handleGetCourses = async () => {
    setIsLoadingOverlay(true);
    await dispatch(
      getCourses({
        limit: DEFAULT_LIMIT,
      })
    )
      .unwrap()
      .then((res) => {
        const options =
          res.data?.responses.map((item) => ({
            value: String(item.id),
            label: item.name,
          })) ?? [];
        setCourseOptions(options);
        if (options.length > 0) {
          setCourseSelected(options[0].value);
        } else {
          setIsLoadingOverlay(false);
        }
      })
      .catch(() => {
        setIsLoadingOverlay(false);
      });
  };

  const handleChangePage = (newPage: number) => {
    if (!courseSelected) return;
    setPage(newPage);
    handleGetStudents({
      courseId: courseSelected,
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
    });
  };

  const renderStudentList = () => {
    if (students.length === 0) {
      return (
        <div className={cx('spinnerContainer')}>
          <Text variant='contentSmall'>{t('common_no_data_available', { ns: 'common/base' })}</Text>
        </div>
      );
    }

    return (
      <>
        <View as='div' className={cx('studentListContainer', 'desktopOnly')}>
          <Table layout='auto'>
            <Table.Head>
              <Table.Row>
                <Table.ColHeader id='Name'>{t('student_name_label', { ns: 'admin/student' })}</Table.ColHeader>
                <Table.ColHeader id='Email'>{t('student_email_label', { ns: 'admin/student' })}</Table.ColHeader>
                <Table.ColHeader id='IdentificationNumber'>
                  {t('student_identification_number_label', { ns: 'admin/student' })}
                </Table.ColHeader>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {students.map((data, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <span>{data.fullName}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span>{data.email}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span>
                      {data.identificationNumber || t('student_identification_number_not_set', { ns: 'admin/student' })}
                    </span>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </View>

        <View className={cx('paginationDesktop')}>
          <BasePagination currentPage={page} hasMore={studentsResponse?.hasMore} onChange={handleChangePage} />
        </View>

        <View as='div' className={cx('mobileOnly')}>
          {students.map((data, index) => (
            <div key={index} className={cx('studentCard')}>
              <div className={cx('cardRow')}>
                <strong>{t('student_name_label', { ns: 'admin/student' })}:</strong>
                <span>{data.fullName}</span>
              </div>
              <div className={cx('cardRow')}>
                <strong>{t('student_email_label', { ns: 'admin/student' })}:</strong>
                <span>{data.email}</span>
              </div>
              <div className={cx('cardRow')}>
                <strong>{t('student_identification_number_label', { ns: 'admin/student' })}:</strong>
                <span>
                  {data.identificationNumber || t('student_identification_number_not_set', { ns: 'admin/student' })}
                </span>
              </div>
            </div>
          ))}
          <BasePagination currentPage={page} hasMore={studentsResponse?.hasMore} onChange={handleChangePage} />
        </View>
      </>
    );
  };

  const renderContent = () => {
    return (
      <View as='div' width={'100%'} height={'100%'}>
        <View as='div' className={cx('courseSelect')}>
          <BaseSelect
            value={courseSelected}
            options={courseOptions}
            onChange={(option) => setCourseSelected(option?.value)}
          />
        </View>

        {renderStudentList()}
      </View>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('studentTitle')}>
          <div className={cx('studentHeaderTitle')}>
            <Heading level='h3'>{t('student_label', { ns: 'admin/student' })}</Heading>
            <img
              className={cx('iconHeader')}
              src={icons.commonIconStudent}
              alt={t('common_alt_img', { ns: 'common/base' })}
            />
          </div>
        </View>

        {renderContent()}
      </div>

      <Loading loading={isLoadingOverlay} overlay={true} />
    </div>
  );
};

export default Student;
